import currencies from 'currency-codes';
import * as R from 'ramda';

import { localizedContentInputs } from '../../components/utils/LocalizedContent/inputsConfig';

const defaultProduct = {
  activationLink: {},
  status: 'DISABLED',
  type: '',
  relatedContents: [],
  genericName: '',
  catalogId: '',
  publisherRefId: '',
  lifeTime: { name: 'PERMANENT', number: 1 },
  physical: false,
  sellingStores: [],
  blackListedCountries: [],
  trialAllowed: false,
  subscriptionTemplate: '',
  trialDuration: '',
  fulfillmentTemplate: '',
  externalContext: '',
  productFamily: '',
  priceFunction: '',
  nextGenerationOf: [],
  resources: [],
  prices: {
    defaultCurrency: '',
  },
};

const defaultManualFulfillment = {
  name: '',
  nexwayProductId: [],
  publisherProductId: [],
  status: 'DISABLED',
  stock: {
    available: 0,
    blacklisted: 0,
    canceled: 0,
    occupied: 0,
    renewed: 0,
  },
  threshold: 0,
};

const defaultStore = {
  status: 'DISABLED',
  name: '',
  emailSenderOverride: '',
  routes: [{ hostname: '' }],
  defaultLocale: '',
  saleLocales: [],
  thankYouDesc: {},
  storeWebsite: '',
  displayName: '',
  blackListedPaymentTypes: [],
  forceGeoipLocalization: false,
  promoteOneClickPayment: false,
  allowOrderDetailsOnCheckoutConfirmation: false,
  recipientCodeMandatory: false,
  allowInstallments: false,
  blackListedCountries: [],
  restrictedCountries: [],
  installmentOptions: [],
  eligibleEndUserTypes: [
    'RESELLER_AUTHENTICATION_REQUIRED',
    'BUYER_AUTHENTICATION_REQUIRED',
    'AUTHENTICATION_NOT_REQUIRED',
  ],
  externalContextGenerationParams: [],
  paymentGroups: {},
  designs: {
    endUserPortal: {
      fontRef: {},
      layoutRef: {},
      themeRef: {},
      i18nRef: {},
    },
    checkout: {
      fontRef: {},
      layoutRef: {},
      themeRef: {},
      i18nRef: {},
      dpThemeRef: {},
    },
    resellerCheckout: {
      fontRef: {},
      layoutRef: {},
      themeRef: {},
      i18nRef: {},
    },
    paymentComponent: {
      rankedPaymentTabsByCountriesList: [{ rankedPaymentTabs: ['credit_card'] }],
    },
  },
};

const defaultProductLocales = {
  localizedLongDesc: {},
  localizedManualRenewalEmailDesc: {},
  localizedPurchaseEmailDesc: {},
  localizedMarketingName: { 'en-US': '' },
  localizedShortDesc: {},
  localizedThankYouDesc: {},
  localizedBulletPointsDesc: {},
};

const localizedValues = [
  'localizedLongDesc',
  'localizedManualRenewalEmailDesc',
  'localizedMarketingName',
  'localizedPurchaseEmailDesc',
  'localizedShortDesc',
  'localizedThankYouDesc',
  'localizedBulletPointsDesc',
];

const inheritanceBrokenFields = ['sellingStores', 'prices'];

// In case of inheritance we need not to copy these values from parent, but to keep them empty
// Only overwritten values must be filled.
const localizedValuesToClearIfInherited = [
  'localizedLongDesc',
  'localizedManualRenewalEmailDesc',
  'localizedPurchaseEmailDesc',
  'localizedShortDesc',
  'localizedThankYouDesc',
  'localizedBulletPointsDesc',
];

const defaultIndependentFields = [
  // 'fulfillmentTemplateName',
  // 'subscriptionTemplate',
  'releaseDate',
  'nextGenerationOf',
  'id',
  'parentId',
  ...inheritanceBrokenFields,
  // 'resources',
  // 'externalContext',
  // 'productFamily',
  // 'priceFunction',
  // 'trialAllowed',
  // 'trialDuration',
];

const unchangableInheritedFields = ['customerId', 'status', 'unifiedRegisterSoftware'];

const productRequiredFields = (product) => {
  let resourcesKeys = null;

  if (product.resources) {
    resourcesKeys = [...product.resources].map((resource, index) => ({
      ...resource,
      index,
    }));
  }

  let { lifeTime } = product;

  if (typeof lifeTime === 'string') {
    const res =
      product.lifeTime !== '7DAY' ? product.lifeTime.match(/[a-zA-Z]+|[0-9]+/g) : ['7DAY'];
    lifeTime = { number: res[1] ? res[0] : '1', name: res[1] ? res[1] : res[0] };
  }

  const newDefault = { ...defaultProduct };

  delete newDefault.fulfillmentTemplate;

  return {
    ...newDefault,
    ...product,
    resources: resourcesKeys || [],
    lifeTime,
  };
};

const productVariationRequiredFields = (product) => {
  const res = product.lifeTime !== '7DAY' ? product.lifeTime.match(/[a-zA-Z]+|[0-9]+/g) : ['7DAY'];
  const lifeTime = { number: res[1] ? res[0] : '1', name: res[1] ? res[1] : res[0] };

  return { ...product, lifeTime };
};

const structureProdAutocompleteSelectOptions = ({
  options,
  optionValue,
  otherOptions,
  optionId,
}) => {
  const res = [];

  if (options?.length) {
    options?.forEach((option) => {
      const id = optionId ? option[optionId] : option.id || option[optionValue];

      if (!res.find((u) => u.id === id)) {
        const newObj = { id, value: `${option[optionValue]}(${option.id})` || id };

        if (otherOptions?.length) {
          otherOptions?.forEach((element) => {
            newObj[element] = option[element];
          });
        }

        res.push(newObj);
      }
    });
  }

  return res;
};

const structureSelectOptions = ({ options, optionValue, otherOptions, optionId, adIddToValue }) => {
  const res = [];

  if (options) {
    options.forEach((option) => {
      const id = optionId ? option[optionId] : option.id || option[optionValue];

      if (!res.find((u) => u.id === id)) {
        const newObj = {
          id,
          value: !adIddToValue ? option[optionValue] : `${option[optionValue]} (${id})` || id,
        };

        if (otherOptions) {
          otherOptions.forEach((element) => {
            newObj[element] = option[element];
          });
        }

        res.push(newObj);
      }
    });
  }

  return res;
};

const groupBy = (objectArray, property) =>
  objectArray.reduce((acc, obj) => {
    const key = obj[property];

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(obj);

    return acc;
  }, {});

const productsVariations = (renewingProducts, productId) =>
  productId
    ? renewingProducts
        ?.filter((item) => item.id === productId)
        .reduce((accumulator, current) => {
          // eslint-disable-next-line
          current.availableVariables = current?.availableVariables?.reduce(
            (acc, curr) => [
              ...acc,
              {
                ...curr,
                fieldValue: current[curr.field],
              },
            ],
            [],
          );

          return [...accumulator, current];
        }, [])[0]
    : {};

const formatePaymentGroups = (array) => {
  const ar = [...array].splice(1);
  const newParams = {};
  const data = groupBy(ar, 'countries');

  Object.keys(data).forEach((key, index) => {
    newParams[index] = {
      countries: key.split(','),
      options: {},
    };

    data[key].forEach((item, ind) => {
      newParams[index].options[ind] = {
        customerType: item.customerType || 'PERSONAL',
        rankedPaymentTabs: item.rankedPaymentTabs,
      };
    });
  });

  return newParams;
};

const defaultStoreLocalObj = { deliveryRemark: '' };

const storeRequiredFields = (store) => {
  const res = { ...defaultStore, ...store };

  if (store.designs) {
    const checkoutObj = {
      ...defaultStore.designs.checkout,
      ...store.designs.checkout,
    };
    const endUserPortalObj = {
      ...defaultStore.designs.endUserPortal,
      ...store.designs.endUserPortal,
    };
    const resellerCheckoutObj = {
      ...defaultStore.designs.resellerCheckout,
      ...store.designs.resellerCheckout,
    };
    const newDesigns = {
      ...store.designs,
      checkout: checkoutObj,
      endUserPortal: endUserPortalObj,
      resellerCheckout: resellerCheckoutObj,
    };

    res.designs = newDesigns;
  }

  if (res.designs.paymentComponent?.rankedPaymentTabsByCountriesList.length > 1) {
    const nwePaymentGroups = formatePaymentGroups(
      res.designs.paymentComponent.rankedPaymentTabsByCountriesList,
    );

    res.paymentGroups = nwePaymentGroups;
  }

  const newBlacklisted = Object.keys(res.blacklistedPaymentMethodsByLayout || {}).map((key) => ({
    layout: key,
    methods: [...res.blacklistedPaymentMethodsByLayout[key]],
  }));
  res.blacklistedPaymentMethodsByLayout = newBlacklisted;

  res.sortedPaymentMethodsByFilters = res.sortedPaymentMethodsByFilters || [];

  if (Object.keys(res.thankYouDesc).length === 0 && res.defaultLocale) {
    res.thankYouDesc = { [res.defaultLocale]: defaultStoreLocalObj };
  } else {
    const newThankYouDesc = {};

    Object.keys(res.thankYouDesc).forEach((elKey) => {
      newThankYouDesc[elKey] = { deliveryRemark: res.thankYouDesc[elKey] };
    });

    res.thankYouDesc = { ...newThankYouDesc };
  }

  return res;
};

const types = {
  array: [],
  string: '',
  boolean: false,
  object: {},
};

const createStandaloneValue = (value, key) => {
  if (!value?.state) return value;

  let valueType = typeof value.value;

  if (valueType === 'object' && Array.isArray(value.value)) {
    valueType = 'array';
  }

  return value?.state === 'inherits' && !inheritanceBrokenFields.includes(key)
    ? types[valueType]
    : value?.value || value || '';
};

const createUnchangableInheritedValue = (value, key) =>
  value?.parentValue && key !== 'status' ? value.parentValue : value || '';

const createInheritableValue = (value, parentValue, key) => {
  // TODO Is isEmpty rule used correctly for '' and {} ?
  const state =
    ((R.isEmpty(value) || R.isNil(value)) && !Array.isArray(value)) ||
    (!localizedContentInputs.includes(key) && R.equals(value, parentValue)) ||
    (parentValue?.defaultCurrency && !value?.defaultCurrency)
      ? 'inherits'
      : 'overrides'; // initial state, user can force after

  return {
    parentValue,
    state,
    value: state === 'inherits' ? parentValue : value,
  };
};

const backToFront = (
  parent,
  resource = defaultProduct,
  independentFields = defaultIndependentFields,
) => {
  // if field in both parent and variant, associate fieldName with properly set inheritable
  if (!parent) return resource;

  const handler = (key, resourceOwn, parentOwn) =>
    createInheritableValue(resourceOwn.value, parentOwn.parentValue, key);

  const inputA = R.mapObjIndexed(
    (value, key) => createInheritableValue(value, undefined, key),
    resource,
  );

  const inputB = R.mapObjIndexed(
    (value, key) => createInheritableValue(undefined, value, key),
    parent || {},
  );

  let iResource = R.mergeWithKey(handler, inputA, inputB);
  // managing fields which cannot inherit
  iResource = R.mapObjIndexed((value, key) => {
    if (independentFields.includes(key)) {
      return createStandaloneValue(value, key);
    }

    if (unchangableInheritedFields.includes(key)) {
      return createUnchangableInheritedValue(value, key);
    }

    return value;
  }, iResource);

  return iResource;
};

// MANDATORY FIELDS
const mandatoryFields = ['lifeTime', 'publisherRefId', 'salesMode'];

const frontToBack = (data) =>
  Object.entries(data).reduce((accumulator, [key, value]) => {
    const tmp = accumulator;
    let newValue;

    switch (true) {
      case !value?.state && !value?.value:
        newValue = value;
        break;
      case value?.state === 'overrides' || (!value?.state && value?.value):
        newValue = value?.value;
        break;
      case value?.state === 'inherits' && value.parentValue !== value.value:
        newValue = value?.parentValue;
        break;
      // no default
    }

    if (mandatoryFields.includes(key) && !newValue) {
      newValue = value?.parentValue;
    }

    if (typeof newValue !== 'undefined') {
      tmp[key] = newValue || '';
    }

    return tmp;
  }, {});

const checkValue = (data) => {
  switch (true) {
    case !data?.state && !data?.value && data?.value !== '':
      return data;
    case data.state === 'inherits':
      return data.parentValue;
    default:
      return data.value;
  }
};

const identityRequiredFields = (identity) => {
  const defaultIdentity = {
    email: '',
    firstName: '',
    lastName: '',
    userName: '',
    clientId: '',
    authorizedCustomerIds: [],
    roleIds: [],
    metaRoleIds: [],
    inactive: true,
  };

  return { ...defaultIdentity, ...identity };
};

const realmRequiredFields = (realm) => {
  const defaultRealm = {
    dbVersion: '',
    id: '',
    jwtPrivateKey: '',
    jwtPublicKey: '',
    lastUpdateReason: '',
    path: '',
    authorizedCustomerIds: [],
  };

  return { ...defaultRealm, ...realm };
};

const countriesOptionsFormatting = (array) =>
  array.map((item) => ({ id: item.alpha2Code, value: item.name }));

const legalEntitiesOptionsFormatting = (array) =>
  array.map((item) => ({ id: item.id, value: item.legalEntityName }));

const languagesOptionsFormatting = (array) =>
  array.map((item) => {
    const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });

    return { id: item.code, value: `${item.code}: ${languageNames.of(item.code)}` };
  });

const notificationRequiredFields = (obj) => {
  const receiverType = obj.emails ? 'email' : 'webhook';
  const defaultObj = {
    name: '',
    status: 'Inactive',
    notificationDefinitionIds: [],
    targetedCustomerIds: [],
    emails: [],
    url: '',
    httpClientConfiguration: {
      httpHeaders: { 'Content-Type': [], Version: [] },
      clientCredentialOauth2Config: {
        clientId: '',
        clientSecret: '',
        tokenUrl: '',
        scopes: [],
      },
      tlsConfiguration: { tlsAuthMode: '' },
    },
  };

  const httpHeaders = {
    ...defaultObj.httpClientConfiguration.httpHeaders,
    ...obj.httpClientConfiguration?.httpHeaders,
  };

  const clientCredentialOauth2Config = {
    ...defaultObj.httpClientConfiguration.clientCredentialOauth2Config,
    ...obj.httpClientConfiguration?.clientCredentialOauth2Config,
  };

  const tlsConfiguration = {
    ...defaultObj.httpClientConfiguration.tlsConfiguration,
    ...obj.httpClientConfiguration?.tlsConfiguration,
  };

  return {
    ...defaultObj,
    ...obj,
    receiverType,
    httpClientConfiguration: { httpHeaders, clientCredentialOauth2Config, tlsConfiguration },
  };
};

const removeEmptyPropsInObject = (data) => {
  const res = { ...data };

  return Object.keys(res).reduce((accumulator, key) => {
    const isObject = typeof res[key] === 'object';
    const isNotEmptyArray = Array.isArray(res[key]) && res[key].length > 0;
    const value = isObject && !isNotEmptyArray ? removeEmptyPropsInObject(res[key]) : res[key];
    const isEmptyObject = isObject && !Object.keys(value).length;

    if (value === '' || isEmptyObject) {
      return accumulator;
    }

    return Object.assign(accumulator, { [key]: value });
  }, {});
};

const generateCurrencies = (items) =>
  items.map(({ code }) => ({
    id: code,
    digits: currencies.code(code)?.digits || 2,
    value: `${code} (${currencies.code(code)?.currency || code})`,
  }));

export {
  storeRequiredFields,
  productRequiredFields,
  structureSelectOptions,
  defaultProduct,
  productsVariations,
  backToFront,
  frontToBack,
  identityRequiredFields,
  checkValue,
  localizedValues,
  countriesOptionsFormatting,
  languagesOptionsFormatting,
  notificationRequiredFields,
  removeEmptyPropsInObject,
  formatePaymentGroups,
  createInheritableValue,
  defaultManualFulfillment,
  defaultProductLocales,
  structureProdAutocompleteSelectOptions,
  realmRequiredFields,
  legalEntitiesOptionsFormatting,
  productVariationRequiredFields,
  generateCurrencies,
  localizedValuesToClearIfInherited,
};
